'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';

interface NavItem {
  label: string;
  href: string;
  children?: { label: string; href: string }[];
}

const navItems: NavItem[] = [
  {
    label: 'Services',
    href: '/services',
    children: [
      { label: 'Web Application Pentesting', href: '/services' },
      { label: 'Mobile App Security Testing', href: '/services' },
      { label: 'API Security Testing', href: '/services' },
      { label: 'Network Security Testing', href: '/services' },
      { label: 'Cloud Security Assessment', href: '/services' },
      { label: 'Red Teaming', href: '/services' },
    ],
  },
  {
    label: 'Compliance',
    href: '/compliance',
    children: [
      { label: 'ISO 27001', href: '/compliance' },
      { label: 'SOC 2', href: '/compliance' },
      { label: 'PCI DSS', href: '/compliance' },
      { label: 'GDPR', href: '/compliance' },
      { label: 'HIPAA', href: '/compliance' },
    ],
  },
  { label: 'About Us', href: '/about' },
  { label: 'Resources', href: '#' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      style={{
        position: 'fixed',
        top: 0,
        width: '100%',
        zIndex: 100,
        background: scrolled ? 'rgba(255,255,255,0.98)' : '#ffffff',
        boxShadow: scrolled ? '0 16px 48px rgba(0,0,0,0.11)' : '0 1px 0 #e5e7eb',
        transition: 'box-shadow 0.18s cubic-bezier(0.4,0,0.2,1)',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {/* Top bar */}
      <div style={{ background: '#0a1d2f', padding: '6px 0' }}>
        <div style={{ maxWidth: '90%', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#9ca3af', fontSize: '12px' }}>🔒 CERT-In Empanelled | ISO 27001 Certified | SOC 2 Attested</span>
          <span style={{ color: '#22c55e', fontSize: '12px', fontWeight: 500 }}>📞 +1 (800) 123-4567 | sales@techlabs.io</span>
        </div>
      </div>

      {/* Main nav */}
      <div style={{ maxWidth: '90%', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '72px' }}>
        {/* Logo */}
        <Link href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', lineHeight: 0 }}>
          <Image
            src="/assets/images/Classy_Elegant_Simple_Name_Logo-1785400291735.png"
            alt="TechLabs - Advanced Cybersecurity Solutions"
            width={160}
            height={48}
            style={{ objectFit: 'contain', height: '48px', width: 'auto' }}
            priority
          />
        </Link>

        {/* Desktop Nav */}
        <nav style={{ display: 'flex', alignItems: 'center', gap: '8px' }} className="hidden lg:flex">
          {navItems.map((item) => (
            <div
              key={item.label}
              style={{ position: 'relative' }}
              onMouseEnter={() => item.children && setActiveDropdown(item.label)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link
                href={item.href}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '8px 14px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#1e1e1e',
                  textDecoration: 'none',
                  borderRadius: '6px',
                  transition: 'background 0.18s',
                }}
                className="hover:bg-green-50"
              >
                {item.label}
                {item.children && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4l4 4 4-4" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </Link>
              {item.children && activeDropdown === item.label && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    boxShadow: '0 16px 48px rgba(0,0,0,0.12)',
                    padding: '8px',
                    minWidth: '220px',
                    zIndex: 200,
                  }}
                >
                  {item.children.map((child) => (
                    <Link
                      key={child.label}
                      href={child.href}
                      style={{
                        display: 'block',
                        padding: '9px 14px',
                        fontSize: '13px',
                        color: '#374151',
                        textDecoration: 'none',
                        borderRadius: '6px',
                        transition: 'background 0.15s, color 0.15s',
                      }}
                      className="hover:bg-green-50 hover:text-green-700"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* CTA */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link
            href="/contact"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '9px 22px',
              background: '#22c55e',
              borderRadius: '25px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#fff',
              textDecoration: 'none',
              transition: 'background 0.18s, box-shadow 0.18s',
              whiteSpace: 'nowrap',
            }}
            className="hover:bg-green-600 hover:shadow-lg"
          >
            Contact Us
          </Link>
          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
            className="flex lg:hidden"
            aria-label="Toggle menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              {mobileOpen ? (
                <path d="M6 18L18 6M6 6l12 12" stroke="#1e1e1e" strokeWidth="2" strokeLinecap="round" />
              ) : (
                <path d="M3 12h18M3 6h18M3 18h18" stroke="#1e1e1e" strokeWidth="2" strokeLinecap="round" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{ background: '#fff', borderTop: '1px solid #e5e7eb', padding: '16px 5%' }}>
          {navItems.map((item) => (
            <Link
              key={item.label}
              href={item.href}
              style={{ display: 'block', padding: '12px 0', fontSize: '15px', fontWeight: 500, color: '#1e1e1e', textDecoration: 'none', borderBottom: '1px solid #f3f4f6' }}
              onClick={() => setMobileOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <Link
            href="/contact"
            style={{ display: 'block', marginTop: '16px', padding: '12px 24px', background: '#22c55e', borderRadius: '25px', textAlign: 'center', color: '#fff', fontWeight: 600, textDecoration: 'none' }}
            onClick={() => setMobileOpen(false)}
          >
            Contact Us
          </Link>
        </div>
      )}
    </header>
  );
}
